import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter,
  Heading2, Heading3, Minus, Undo2, Redo2, Upload, FileText, Video, Paperclip,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function MenuBar({ editor, onUpload }) {
  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt("Enter image URL:");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const btn = (active, onClick, Icon, title) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-slate-200 bg-slate-50/80" data-testid="email-editor-toolbar">
      {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Heading 2")}
      {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "Heading 3")}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold")}
      {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic")}
      {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), UnderlineIcon, "Underline")}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bullet List")}
      {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered List")}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(editor.isActive({ textAlign: "left" }), () => editor.chain().focus().setTextAlign("left").run(), AlignLeft, "Align Left")}
      {btn(editor.isActive({ textAlign: "center" }), () => editor.chain().focus().setTextAlign("center").run(), AlignCenter, "Align Center")}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(editor.isActive("link"), setLink, LinkIcon, "Insert Link")}
      {btn(false, addImage, ImageIcon, "Insert Image URL")}
      {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "Horizontal Rule")}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <button
        type="button"
        onClick={onUpload}
        title="Upload File (Image, Video, Document)"
        className="flex items-center gap-1 px-2 py-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors text-xs font-medium"
        data-testid="email-editor-upload-btn"
      >
        <Upload className="w-4 h-4" />
        Upload
      </button>
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo")}
      {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo")}
    </div>
  );
}

export default function EmailRichEditor({ value, onChange, variables = [] }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { style: "color:#14b8a6;" } }),
      Image.configure({ HTMLAttributes: { style: "max-width:100%;height:auto;border-radius:8px;margin:12px 0;" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[280px] px-4 py-3",
        style: "font-family:Arial,Helvetica,sans-serif;color:#334155;line-height:1.7;font-size:15px;",
      },
    },
  });

  // Sync external value changes (e.g. template switch)
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentHTML = editor.getHTML();
      // Only update if substantially different (avoids cursor jump)
      if (currentHTML !== value && Math.abs(currentHTML.length - (value || "").length) > 5) {
        editor.commands.setContent(value || "");
      }
    }
  }, [value, editor]);

  const handleFileUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxSize = 25 * 1024 * 1024; // 25MB per file

    setUploading(true);
    const uploaded = { images: [], docs: [] };

    for (const file of files) {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large (max 25MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);

        const token = localStorage.getItem("token");
        const resp = await fetch(`${API}/api/upload?folder=email-assets`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!resp.ok) throw new Error("Upload failed");
        const data = await resp.json();
        const fileUrl = data.url || data.file_url;

        if (!fileUrl) throw new Error("No URL returned");

        const isImage = file.type.startsWith("image/");

        if (isImage) {
          uploaded.images.push(fileUrl);
        } else {
          const isVideo = file.type.startsWith("video/");
          const icon = isVideo ? "&#9654;" : "&#128196;";
          const label = isVideo ? "Video" : (file.name.endsWith(".pdf") ? "PDF" : "DOC");
          uploaded.docs.push({ url: fileUrl, name: file.name, icon, label, size: file.size });
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    // Append everything at the end: images first, then documents
    if (uploaded.images.length > 0 || uploaded.docs.length > 0) {
      let appendHtml = "";

      // Images first
      for (const url of uploaded.images) {
        appendHtml += `<img src="${url}" style="max-width:100%;height:auto;border-radius:8px;margin:12px 0;" />`;
      }

      // Then documents/videos
      for (const doc of uploaded.docs) {
        appendHtml += `<p><a href="${doc.url}" style="color:#14b8a6;font-weight:600;">${doc.icon} ${doc.name}</a> <span style="color:#94a3b8;font-size:13px;">(${doc.label} - ${(doc.size / 1024).toFixed(0)}KB)</span></p>`;
      }

      // Get current content and append at the end
      const currentHtml = editor.getHTML();
      editor.commands.setContent(currentHtml + appendHtml);
      onChange(editor.getHTML());

      const total = uploaded.images.length + uploaded.docs.length;
      toast.success(`${total} file${total > 1 ? "s" : ""} added to bottom`);
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [editor]);

  const insertVariable = (v) => {
    editor?.chain().focus().insertContent(`{{${v}}}`).run();
  };

  return (
    <div data-testid="email-rich-editor">
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <MenuBar editor={editor} onUpload={() => fileInputRef.current?.click()} />
        {uploading && (
          <div className="px-4 py-2 bg-teal/5 text-teal text-xs font-medium flex items-center gap-2 border-b border-slate-200">
            <div className="w-3 h-3 border-2 border-teal border-t-transparent rounded-full animate-spin" />
            Uploading file...
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        className="hidden"
        onChange={handleFileUpload}
        data-testid="email-editor-file-input"
      />
      {/* Variables */}
      {variables.length > 0 && (
        <div className="mt-3 bg-slate-50 rounded-lg border border-slate-200 p-3" data-testid="email-editor-variables">
          <p className="text-xs font-semibold text-navy mb-2">Click to insert variable:</p>
          <div className="flex flex-wrap gap-1.5">
            {variables.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => insertVariable(v)}
                className="px-2 py-0.5 bg-white border border-slate-200 text-xs rounded-full text-slate-600 hover:bg-teal hover:text-white hover:border-teal transition-colors cursor-pointer"
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
